import { Environment } from "@axelar-network/axelarjs-sdk";
import { SigningCosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import { OfflineSigner, StdSignature } from "@cosmjs/launchpad";
import { GasPrice } from "@cosmjs/stargate";
import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";

import { supportedChainsMap } from "../../config";
import { ICosmosConnector } from "../../interfaces";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Window extends KeplrWindow {}
}

// Method taken from https://github.com/chainapsis/keplr-wallet/blob/4726a96b9663f17b91c5d6b0448bf85ebb4a678a/packages/stores/src/account/get-keplr.ts
const getKeplrFromWindow: () => Promise<Keplr | undefined> = async () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (window.keplr) {
    return window.keplr;
  }

  if (document.readyState === "complete") {
    return window.keplr;
  }

  return new Promise((resolve) => {
    const documentStateChange = (event: Event) => {
      if (
        event.target &&
        (event.target as Document).readyState === "complete"
      ) {
        resolve(window.keplr);
        document.removeEventListener("readystatechange", documentStateChange);
      }
    };

    document.addEventListener("readystatechange", documentStateChange);
  });
};

class KeplrConnector implements ICosmosConnector {
  cosmJsProviderHooks: ICosmosConnector["cosmJsProviderHooks"];

  async activate(chainId: string) {
    const keplrFromWindow = await getKeplrFromWindow();
    if (!keplrFromWindow) {
      throw new Error(
        "Keplr Extension is missing. Please install Keplr extension first.",
      );
    }
    if (!this.cosmJsProviderHooks) {
      throw new Error("CosmJs Provider missing hooks.");
    }

    if (supportedChainsMap.cosmos[chainId]?.chainType === Environment.TESTNET) {
      if (supportedChainsMap.cosmos[chainId]?.testnetChainInfo) {
        await keplrFromWindow.experimentalSuggestChain(
          supportedChainsMap.cosmos[chainId].testnetChainInfo,
        );
      }
    }

    await keplrFromWindow.enable(chainId);

    const info = await keplrFromWindow.getKey(chainId);
    const offlineSigner = await keplrFromWindow.getOfflineSignerAuto(chainId);
    const signingCosmWasmClient = await SigningCosmWasmClient.connectWithSigner(
      supportedChainsMap.cosmos[chainId]?.rpcUrl,
      offlineSigner as OfflineSigner,
      {
        gasPrice: GasPrice.fromString(
          supportedChainsMap.cosmos[chainId]?.gasPrice,
        ),
      },
    );

    this.cosmJsProviderHooks.setChainId(chainId);
    this.cosmJsProviderHooks.setAddress(info.bech32Address);
    this.cosmJsProviderHooks.setProvider(signingCosmWasmClient);
    this.cosmJsProviderHooks.setMainConnector(this);
  }

  async connectEagerly(chainId: string): Promise<void> {
    return this.activate(chainId);
  }

  async deactivate(): Promise<void> {
    if (!this.cosmJsProviderHooks) {
      throw new Error("CosmJs Provider missing hooks.");
    }

    this.cosmJsProviderHooks.setChainId(undefined);
    this.cosmJsProviderHooks.setAddress(undefined);
    this.cosmJsProviderHooks.setProvider(undefined);
  }

  async getCosmosAddressFromPublicKey(
    chainId: string,
    prefix: string,
  ): Promise<string> {
    const keplrFromWindow = await getKeplrFromWindow();
    if (!keplrFromWindow) {
      throw new Error(
        "Keplr Extension is missing. Please install Keplr extension first.",
      );
    }
    const key = await keplrFromWindow.getKey(chainId);
    const { Bech32Address } = await import("@keplr-wallet/cosmos");
    const { PubKeySecp256k1 } = await import("@keplr-wallet/crypto");
    return new Bech32Address(
      new PubKeySecp256k1(key.pubKey).getAddress(),
    ).toBech32(prefix);
  }

  async signMessage(
    message: string,
    chainId: string = "cosmos-1",
  ): Promise<StdSignature> {
    const keplrFromWindow = await getKeplrFromWindow();
    if (!keplrFromWindow) {
      throw new Error(
        "Keplr Extension is missing. Please install Keplr extension first.",
      );
    }
    const offlineSigner = keplrFromWindow.getOfflineSigner(chainId);
    const accounts = await offlineSigner.getAccounts();
    const signature = await keplrFromWindow.signArbitrary(
      chainId,
      accounts[0].address,
      message,
    );
    return signature;
  }
}

export const keplr = new KeplrConnector();
